// Input controller — extracted verbatim from GameScene.ts (FACE&FURY S2 SPLIT).
// Behavior-frozen: method bodies are byte-identical to the monolith; only the signatures were
// rewritten to `export function name(this: GameScene, ...)` and bound back onto the scene as fields.
import Phaser from 'phaser';
import { type Direction, neighborId, passable } from '../../core';
import { dominantDir, isStraightDrag, glideStepCount, clampGlidePath } from '../dragGlide';
import { PALETTE } from '../keys';
import { PLAYER, MOVE_TO_CHOMP_GUARD_MS, type InputCmd } from './types';
import type { GameScene } from '../scenes/GameScene';



  // --- input ---------------------------------------------------------------
export function bindInput(this: GameScene): void {
    const kb = this.input.keyboard;
    if (kb) {
      // A keypress ALWAYS wins instantly and cancels any in-flight drag-glide (playerKey wrapper).
      kb.on('keydown-UP', () => this.playerKey('up'));
      kb.on('keydown-DOWN', () => this.playerKey('down'));
      kb.on('keydown-LEFT', () => this.playerKey('left'));
      kb.on('keydown-RIGHT', () => this.playerKey('right'));
      kb.on('keydown-W', () => this.playerKey('up'));
      kb.on('keydown-S', () => this.playerKey('down'));
      kb.on('keydown-A', () => this.playerKey('left'));
      kb.on('keydown-D', () => this.playerKey('right'));
      kb.on('keydown-SPACE', () => this.playerKey('munch'));
      kb.on('keydown-ENTER', () => this.playerKey('munch'));
      // SCAN (information verb). S is taken by WASD-down, so bind K (and Q as a friendly alias).
      kb.on('keydown-K', () => this.playerKey('scan'));
      kb.on('keydown-Q', () => this.playerKey('scan'));
      // PAUSE (ESC / P) — toggles the pause menu.
      kb.on('keydown-ESC', () => this.togglePause());
      kb.on('keydown-P', () => this.togglePause());
    }

    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      this.cancelPendingSteps(); // a new gesture aborts any in-flight glide
      this.pointerDown = { x: p.x, y: p.y, t: this.time.now };
    });
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => this.onPointerMove(p));
    this.input.on('pointerup', (p: Phaser.Input.Pointer) => this.onPointerUp(p));
  }


  /** Keyboard/manual player intent: a keypress ALWAYS wins instantly and cancels any in-flight glide. */
export function playerKey(this: GameScene, cmd: InputCmd): void {
    this.cancelPendingSteps();
    this.dispatch(cmd);
  }


export function onPointerUp(this: GameScene, p: Phaser.Input.Pointer): void {
    const start = this.pointerDown;
    this.pointerDown = null;
    this.clearGlidePreview();
    if (!start) return;
    const dx = p.x - start.x;
    const dy = p.y - start.y;
    const dist = Math.hypot(dx, dy);
    const swipeMin = Math.max(24, Math.min(this.cellW, this.cellH) * 0.5);

    if (dist >= swipeMin) {
      // DRAG/SWIPE. Dominant axis picks the direction; a clearly-straight drag becomes a multi-step glide.
      const dir = dominantDir(dx, dy);
      // Not playing yet / paused / ended: single dispatch (start-round / no-op path), never queue a run.
      if (this.preRoundActive || this.paused || this.ended) {
        this.dispatch(dir);
        return;
      }
      // Non-straight (diagonal-ish) -> today's behavior: exactly one dominant-axis step.
      if (!isStraightDrag(dx, dy)) {
        this.cancelPendingSteps();
        this.dispatch(dir);
        this.lastPointerMoveMs = this.time.now; // arms the own-cell tap->CHOMP mis-tap guard
        return;
      }
      // Straight -> multi-step glide. N = cells spanned along the dominant axis (a relative gesture,
      // like today's swipe), clamped to the axis length; N === 1 collapses to exactly one step.
      const n = glideStepCount(dx, dy, this.cellW, this.cellH, this.cols, this.rows);
      this.beginGlide(dir, n);
      return;
    }

    // Tap. Pre-round / paused / ended: route through dispatch() so the coach's tap-to-start
    // short-circuit fires for ANY tap (dispatch() no-ops safely when paused/ended).
    if (this.preRoundActive || this.paused || this.ended) {
      this.dispatch('munch');
      return;
    }
    const snap = this.sim.snapshot();
    const human = snap.players.find((pl) => pl.id === PLAYER);
    if (!human) return;
    const tapped = this.cellIdAt(p.x, p.y);
    if (tapped === null) return;
    if (tapped === human.pos) {
      // OWN cell — SINGLE-tap CHOMP (owner playtest 2026-07-02: the old double-tap read as
      // "clanky"). Two mis-tap guards keep it honest: (1) drag-ends never reach here — the
      // dist >= swipeMin branch above owns them; (2) a tap landing within MOVE_TO_CHOMP_GUARD_MS
      // of a pointer-driven move step (movement tap-storm) is swallowed, never CHOMPed.
      if (this.time.now - this.lastPointerMoveMs < MOVE_TO_CHOMP_GUARD_MS) return;
      this.dispatch('munch'); // SAME funnel as Space/Enter/dock button
      // Instant ack (<90ms): pulse the dock CHOMP button NOW; the eat/mistake event re-colors it.
      this.pulseDockButton(this.munchButton, PALETTE.correct, 300);
      return;
    }
    // ANY other cell — tap-anywhere autopath: BFS the LIVE Core state (walls + armed cells block,
    // exactly like Core's move rule; un-eaten decoys ARE passable — routing THROUGH them and arming
    // them on pass-through is the Gauntlet verb, so paths may deliberately cross decoys) and feed
    // the route through the SAME paced pending-steps funnel as a drag-glide. Movement only moves:
    // arriving on the tapped cell NEVER auto-CHOMPs (CHOMP-only commitment is a design pillar).
    const dirs = this.pathDirsTo(human.pos, tapped);
    if (dirs && dirs.length > 0) this.beginPathWalk(dirs);
    else this.showCommandHint('No open path — walls and LOCKED tiles block the way.');
  }


  /**
   * Begin a straight-line multi-step glide: N repeats of one direction through the shared
   * pending-steps funnel (see beginPathWalk).
   */
export function beginGlide(this: GameScene, dir: Direction, n: number): void {
    this.beginPathWalk(new Array<Direction>(n).fill(dir));
  }


  /**
   * Begin a queued run of single-step directions (straight-drag glide OR tap-anywhere autopath):
   * feed ONE single-step 'move' Command per STEP_FEED_MS through the SAME dispatch funnel the
   * AI/keyboard use, re-validating each step against LIVE Core state. Replaces any in-flight run;
   * the FIRST step fires now, so cell 1 is as snappy as a single swipe (zero added latency).
   */
export function beginPathWalk(this: GameScene, dirs: Direction[]): void {
    this.cancelPendingSteps(); // replace any in-flight run
    this.pendingSteps = dirs.slice();
    this.releasePendingStep();
  }


export function cancelPendingSteps(this: GameScene): void {
    this.pendingSteps = [];
    this.stepFeedAccumMs = 0;
  }


  /**
   * Feed the next queued step, re-validated against LIVE state. Returns false when the run is
   * clamped or cancelled. Wall/edge/armed-decoy clamp uses the EXPORTED core neighborId()/passable()
   * so it can never drift from Core's own move rule (rules.ts) — a run whose NEXT step became
   * impassable mid-walk (arming, hazards) STOPS rather than re-routing or pushing through.
   * Stun/death/pause/board-end abort the route rather than sleep-walking.
   */
export function releasePendingStep(this: GameScene): boolean {
    const dir = this.pendingSteps[0];
    if (!dir) {
      this.cancelPendingSteps();
      return false;
    }
    if (this.ended || this.paused || this.preRoundActive) {
      this.cancelPendingSteps();
      return false;
    }
    const me = this.sim.players[PLAYER];
    if (!me || !me.alive || this.sim.timeMs < me.stunnedUntilMs) {
      this.cancelPendingSteps(); // stun / hazard / death aborts the route rather than sleep-walking
      return false;
    }
    // Re-validate the very next step against LIVE state via the shared clamp (EDGE / WALL / armed-decoy),
    // so mid-run arming/hazards can't push the route through a now-impassable cell.
    const { path } = clampGlidePath(this.sim.state, me.pos, dir, 1);
    if (path.length === 0) {
      this.cancelPendingSteps();
      return false;
    }
    this.pendingSteps.shift();
    // DIRECT dispatch (NOT the cancelling playerKey wrapper) so the run never cancels itself.
    this.dispatch(dir);
    this.lastPointerMoveMs = this.time.now; // arms the own-cell tap->CHOMP mis-tap guard
    if (this.pendingSteps.length === 0) this.cancelPendingSteps();
    return true;
  }


  /**
   * Live path preview (legibility, reduceMotion-safe): while a straight drag is held, tint the cells
   * the glide would reach and mark the wall/edge/armed cell that STOPS it, so the clamp is taught
   * before commit. Pure overlay — redraws only on finger move (no autonomous motion), never affects
   * determinism. A diagonal-ish drag previews a single step (exactly what a release will do).
   */
export function onPointerMove(this: GameScene, p: Phaser.Input.Pointer): void {
    const start = this.pointerDown;
    if (!start || this.preRoundActive || this.paused || this.ended) {
      this.clearGlidePreview();
      return;
    }
    const dx = p.x - start.x;
    const dy = p.y - start.y;
    const dist = Math.hypot(dx, dy);
    const swipeMin = Math.max(24, Math.min(this.cellW, this.cellH) * 0.5);
    if (dist < swipeMin) {
      this.clearGlidePreview();
      return;
    }
    const me = this.sim.players[PLAYER];
    if (!me || !me.alive) {
      this.clearGlidePreview();
      return;
    }
    // Same derivation + clamp a release will use (glideStepCount returns 1 for a diagonal-ish drag, so
    // the preview shows exactly the single step that would fire), single-sourced from dragGlide.
    const dir = dominantDir(dx, dy);
    const want = glideStepCount(dx, dy, this.cellW, this.cellH, this.cols, this.rows);
    const { path, blocked } = clampGlidePath(this.sim.state, me.pos, dir, want);
    this.drawGlidePreview(path, blocked);
  }


export function drawGlidePreview(this: GameScene, path: number[], blocked: number | null): void {
    const g = this.glidePreview ?? (this.glidePreview = this.add.graphics().setDepth(7));
    g.clear();
    const pad = 5;
    const w = this.cellW - pad * 2;
    const h = this.cellH - pad * 2;
    for (let i = 0; i < path.length; i++) {
      const c = this.cellCenter(path[i]!);
      g.fillStyle(PALETTE.player, Math.max(0.1, 0.24 - i * 0.02)); // near cell reads brightest.
      g.fillRoundedRect(c.x - this.cellW / 2 + pad, c.y - this.cellH / 2 + pad, w, h, 8);
    }
    if (path.length > 0) {
      const c = this.cellCenter(path[path.length - 1]!);
      g.lineStyle(3, PALETTE.player, 0.9); // outline the resting cell so the stop point is unmistakable.
      g.strokeRoundedRect(c.x - this.cellW / 2 + pad, c.y - this.cellH / 2 + pad, w, h, 8);
    }
    if (blocked !== null) {
      const c = this.cellCenter(blocked);
      g.lineStyle(3, PALETTE.hazard, 0.9); // mark the wall/edge/armed cell that STOPS the run.
      g.strokeRoundedRect(c.x - this.cellW / 2 + pad, c.y - this.cellH / 2 + pad, w, h, 8);
      const s = Math.min(this.cellW, this.cellH) * 0.22;
      g.lineBetween(c.x - s, c.y - s, c.x + s, c.y + s);
      g.lineBetween(c.x - s, c.y + s, c.x + s, c.y - s);
    }
  }


export function clearGlidePreview(this: GameScene): void {
    this.glidePreview?.clear();
  }


export function cellIdAt(this: GameScene, x: number, y: number): number | null {
    const col = Math.floor((x - this.gridX) / this.cellW);
    const row = Math.floor((y - this.gridY) / this.cellH);
    if (col < 0 || row < 0 || col >= this.cols || row >= this.rows) return null;
    return row * this.cols + col;
  }


  /**
   * Tap-anywhere autopath: BFS over the LIVE Core state from `from` to `to`, using the SAME exported
   * core helpers as the glide clamp (neighborId/passable — rules.ts is the single source of the move
   * rule: walls + armed un-eaten cells block; un-eaten decoys are passable BY DESIGN, arming on
   * pass-through is the Gauntlet verb the SKIPPED pill teaches). Deterministic (fixed direction
   * order, plain FIFO — but renderer-only regardless: every resulting step still goes through
   * dispatch()). Returns the step directions of a shortest path, or null when `to` is unreachable
   * or itself impassable. Grid is ≤36 cells, so a per-tap BFS is negligible.
   */
export function pathDirsTo(this: GameScene, from: number, to: number): Direction[] | null {
    const state = this.sim.state;
    if (from === to || !passable(state, to)) return null;
    const DIRS: readonly Direction[] = ['up', 'left', 'right', 'down'];
    const cameFrom = new Map<number, { at: number; dir: Direction }>();
    const queue: number[] = [from];
    const seen = new Set<number>([from]);
    while (queue.length > 0) {
      const cur = queue.shift()!;
      if (cur === to) break;
      for (const d of DIRS) {
        const nx = neighborId(cur, d, state.rows, state.cols);
        if (nx === null || seen.has(nx) || !passable(state, nx)) continue;
        seen.add(nx);
        cameFrom.set(nx, { at: cur, dir: d });
        queue.push(nx);
      }
    }
    if (!cameFrom.has(to)) return null;
    const dirs: Direction[] = [];
    let cur = to;
    while (cur !== from) {
      const step = cameFrom.get(cur);
      if (!step) return null;
      dirs.push(step.dir);
      cur = step.at;
    }
    return dirs.reverse();
  }
